import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from "react";

type MobileHeaderMode = "default" | "back-only" | "tall" | "sub-tall";

interface PageTitleState {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  leftActions?: ReactNode;
  /** Mobile-only override for the right-side action(s). Falls back to `actions`. */
  mobileActions?: ReactNode;
  /** Mobile-only override for the title. Falls back to `title`. */
  mobileTitle?: string;
  hideMobileDock?: boolean;
  mobileHeaderMode?: MobileHeaderMode;
  onMobileBack?: () => void;
  hideMobileTopBar?: boolean;
  hideDesktopTopBar?: boolean;
  desktopBackOnly?: boolean;
}




const defaultState: PageTitleState = { title: "" };

/**
 * El «testigo» de la cabecera: la identidad de quien escribió lo que hay pintado.
 *
 * Es un objeto vacío a propósito, y no una cadena con el título o la ruta: dos
 * pantallas pueden llamarse igual (dos fichas de producto, la misma página
 * remontada al cambiar de empresa) y entonces una cadena las confundiría. Un
 * objeto solo es igual a sí mismo, así que cada montaje tiene el suyo y nadie
 * puede suplantarlo.
 */
type PageTitleOwner = object;

interface PageTitleContextValue {
  state: PageTitleState;
  /** Escribe la cabecera y anota que ese estado es de `owner`. */
  claim: (owner: PageTitleOwner, next: PageTitleState) => void;
  /** Borra la cabecera SOLO si el testigo guardado sigue siendo el de `owner`. */
  release: (owner: PageTitleOwner) => void;
}

const PageTitleContext = createContext<PageTitleContextValue>({
  state: defaultState,
  claim: () => {},
  release: () => {},
});

export function PageTitleProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PageTitleState>(defaultState);
  /**
   * Quién es el dueño del estado que hay pintado ahora mismo. Va en una ref y no
   * en el estado porque no tiene que provocar ningún repintado por sí solo: es
   * una anotación al margen, no algo que se vea.
   */
  const ownerRef = useRef<PageTitleOwner | null>(null);

  const claim = useCallback((owner: PageTitleOwner, next: PageTitleState) => {
    ownerRef.current = owner;
    setState(next);
  }, []);

  const release = useCallback((owner: PageTitleOwner) => {
    // Si el testigo ya no es suyo, otra pantalla escribió después: no se toca
    // nada. Ese `return` es todo el arreglo del parpadeo de la cabecera.
    if (ownerRef.current !== owner) return;
    ownerRef.current = null;
    setState(defaultState);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.classList.toggle("dock-hidden", !!state.hideMobileDock);
    return () => {
      document.body.classList.remove("dock-hidden");
    };
  }, [state.hideMobileDock]);

  return (
    <PageTitleContext.Provider value={{ state, claim, release }}>
      {children}
    </PageTitleContext.Provider>
  );
}

/**
 * Declara el título y el cromo de la cabecera de una pantalla.
 *
 * EL ORDEN DE LOS EFECTOS, que es lo que aquí nadie recuerda y lo que hacía que
 * la cabecera diera un salto al navegar.
 *
 * Cuando se pasa de la pantalla A a la B, React NO ejecuta «lo de A y luego lo
 * de B» como dos cosas separadas: dentro de un mismo commit ejecuta PRIMERO
 * todas las limpiezas (la de A) y DESPUÉS todos los efectos (el de B). Con el
 * código anterior —`return () => setState(defaultState)`— esa secuencia era:
 * limpieza de A deja el estado en blanco, efecto de B lo vuelve a llenar. Si las
 * dos cosas caen en el mismo commit, React agrupa los dos `setState` y no se
 * pinta el intermedio; pero en cuanto caen en commits distintos —y aquí caen a
 * menudo, porque las rutas van con `lazy()` y entre medias hay un `<Suspense>`,
 * o porque la pantalla nueva monta ANTES de que la vieja termine de irse— el
 * blanco de A llegaba a pintarse, o peor: la limpieza de A llegaba TARDE y
 * borraba la cabecera que B ya había escrito, y entonces no era un parpadeo sino
 * una cabecera vacía que se quedaba así.
 *
 * De ahí el testigo. Al escribir, esta pantalla se queda con una marca propia
 * (`owner`, estable durante todo el montaje). Al desmontar solo borra si la
 * marca guardada en el proveedor SIGUE SIENDO LA SUYA. Si B ya escribió, la
 * marca es de B y la limpieza de A no toca nada, llegue cuando llegue.
 *
 * LO QUE EL TESTIGO NO ARREGLA, y conviene saberlo antes de dar el hueco por
 * cerrado: si A se desmonta y B tarda en llegar, nadie escribe la cabecera
 * durante esa espera y el estado se queda en `defaultState`. Y ese hueco no es
 * raro — `shell/PageTransition` usa `<AnimatePresence mode="wait">`, que
 * desmonta A ENTERA antes de montar B, y todas las rutas van con `lazy()`, así
 * que la primera visita a cada ruta se pasa la descarga del chunk sin dueño.
 *
 * De eso, este cambio arregla la mitad: las variantes miden ya lo mismo
 * (`MobileTopBar`), así que la barra ya no encoge 34 px y vuelve a crecer. La
 * otra mitad sigue ahí y es de otra clase: sin modo se pinta la variante por
 * defecto, que enseña el AVATAR donde la subpágina tenía la flecha de atrás. Si
 * alguien toca ahí durante la espera, abre la hoja de perfil en vez de volver.
 *
 * No se arregla desde aquí y por eso se deja escrito: quitar el avatar cuando no
 * hay título se lo quitaría también a las pantallas que nunca llaman a este hook
 * (Ajustes › Cuenta, Facturación, Regional, Informes, el carrito, los pedidos…),
 * que hoy lo usan como su único acceso al perfil. Cerrarlo de verdad es cosa de
 * `PageTransition`/`RoleBasedRouter`: o el modo viaja con la ruta, o la pantalla
 * vieja no se va hasta que la nueva está lista.
 */
export function usePageTitle(
  title: string,
  subtitle?: string,
  actions?: ReactNode,
  leftActions?: ReactNode,
  options?: {
    hideMobileDock?: boolean;
    mobileHeaderMode?: MobileHeaderMode;
    onMobileBack?: () => void;
    hideMobileTopBar?: boolean;
    hideDesktopTopBar?: boolean;
    desktopBackOnly?: boolean;
    mobileActions?: ReactNode;
    mobileTitle?: string;
  }
) {
  const { claim, release } = useContext(PageTitleContext);
  /**
   * La marca de este montaje. Se crea una sola vez y no cambia aunque el efecto
   * se vuelva a ejecutar porque cambió el título o las acciones: lo que importa
   * es quién es la pantalla, no cuántas veces ha escrito.
   */
  const ownerRef = useRef<PageTitleOwner | null>(null);
  if (ownerRef.current === null) ownerRef.current = {};
  const owner = ownerRef.current;
  const hideMobileDock = options?.hideMobileDock;
  const mobileHeaderMode = options?.mobileHeaderMode;
  const onMobileBack = options?.onMobileBack;
  const hideMobileTopBar = options?.hideMobileTopBar;
  const hideDesktopTopBar = options?.hideDesktopTopBar;
  const desktopBackOnly = options?.desktopBackOnly;
  const mobileActions = options?.mobileActions;
  const mobileTitle = options?.mobileTitle;
  useEffect(() => {
    if (title) {
      claim(owner, { title, subtitle, actions, leftActions, hideMobileDock, mobileHeaderMode, onMobileBack, hideMobileTopBar, hideDesktopTopBar, desktopBackOnly, mobileActions, mobileTitle });
      return () => release(owner);
    }
  }, [title, subtitle, actions, leftActions, hideMobileDock, mobileHeaderMode, onMobileBack, hideMobileTopBar, hideDesktopTopBar, desktopBackOnly, mobileActions, mobileTitle, claim, release, owner]);
}


export function useCurrentPageTitle() {
  return useContext(PageTitleContext).state;
}
